import { useTranslation } from "react-i18next";
import { PAYMENT_PROVIDERS, type ProviderId } from "@/constants";
import { Card, PaymentOption } from "@/components/kit";
import clickLogo from "@/assets/logo/click_logo_new.svg";
import paymeLogo from "@/assets/logo/payme_logo.png";
import uzumLogo from "@/assets/logo/uzum_logo.png";
import paynetLogo from "@/assets/logo/paynet_logo.jpg";

/** Provayder → rasmiy logo (yuklangan assetlar). */
const PAYMENT_LOGOS: Record<string, string> = {
  click: clickLogo,
  payme: paymeLogo,
  paymee: paymeLogo,
  uzum: uzumLogo,
  paynet: paynetLogo,
};

/**
 * Umumiy (bot) selektorida hali "Tez orada" bilan disabled ko'rsatiladigan
 * provayderlar — global `available: true` bo'lsa ham (o'z domenida, mas.
 * paynet.insonuchun.uz / zoomrad.insonuchun.uz, selektor umuman ko'rinmaydi
 * va to'lov to'liq ishlaydi).
 */
const COMING_SOON_IN_SELECTOR = new Set<ProviderId>(["paynet", "zoomrad"]);

function PaymentLogo({ id }: { id: string }) {
  const src = PAYMENT_LOGOS[id];
  if (!src) {
    return <span className="size-7 rounded-lg" style={{ background: "var(--c-line)" }} aria-hidden />;
  }
  return <img src={src} alt="" className="size-7 rounded-lg object-contain" loading="lazy" />;
}

interface PaymentMethodsProps {
  value: ProviderId;
  onChange: (id: ProviderId) => void;
  /** "To'lov turini tanlang" sarlavhasini ko'rsatish (default: true) */
  showTitle?: boolean;
  className?: string;
}

/**
 * To'lov usullari ro'yxati — Figma §5.10 / §6.2.
 * Oq karta ichida vertikal radio qatorlar: Click / Payme / Uzum (logo + radio).
 * Uzum disabled bo'lsa "Tez orada". ChildDetailPage va BazaarView birga ishlatadi.
 */
export function PaymentMethods({ value, onChange, showTitle = true, className }: PaymentMethodsProps) {
  const { t } = useTranslation();
  // Domenga bog'liq provayderlar (Click/Payme/Zoomrad) o'z hostida fixed bo'lib,
  // bu komponent umuman ko'rsatilmaydi (showPaymentSelector orqali) — shu yerga
  // yetib kelgan bo'lsa, hammasi tanlovga ochiq.
  const providers = PAYMENT_PROVIDERS;

  return (
    <Card className={`overflow-hidden ${className ?? ""}`}>
      {showTitle && (
        <h2 className="px-4 pt-4 text-base font-semibold" style={{ color: "var(--c-text)" }}>
          {t("selectPaymentTitle")}
        </h2>
      )}
      <div className="mt-1 [&>*+*]:border-t [&>*+*]:border-[var(--c-line)]">
        {providers.map((p) => {
          const disabled = !p.available || COMING_SOON_IN_SELECTOR.has(p.id);
          return (
            <PaymentOption
              key={p.id}
              logo={<PaymentLogo id={p.id} />}
              name={p.label}
              selected={value === p.id}
              disabled={disabled}
              hint={t("paymentComingSoon")}
              onSelect={() => !disabled && onChange(p.id)}
            />
          );
        })}
      </div>
    </Card>
  );
}
